import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../../../../../shared/components/ui/Modal';
import Button from '../../../../../shared/components/ui/Button';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { getReportsHotLeadsNotConverted } from '../../../../services/api/implementation/impl';
import { formatIndianCurrency } from '../../../../../shared/utils/CurrencyFormat';

const Section1 = ({
  mode,
  collapsedSections,
  renderSectionHeader,
  activeTab,
  setActiveTab,
  targetAchievementData,
  isLoadingTargetAchievement,
  projectID,
  allowActions,
  month,
  canAddReason,
  allReasonsData,
  isLoadingReasons,
  handleOpenReasonModal,
  onSubmitReasonInline,
  isSubmittingReason,
  sectionNote
}) => {
  const userID = Number(Cookies.get('userId'));

  // Transform API data to match component structure
  const transformTargetAchievementData = (apiData) => {
    if (!apiData) return null;

    // Handle different possible API response structures
    const data = apiData?.data || apiData || [];

    // If it's an array, map each item
    if (Array.isArray(data)) {
      return data.map((item) => {
        // Try to extract data from different possible structures
        const name =
          item?.name || item?.executiveName || item?.userName || item?.user?.name || 'Unknown';
        const level = item?.level || null;
        const newBookingAchieved = item?.newBooking?.achieved || item?.bookingAchieved || 0;
        const newBookingTarget = item?.newBooking?.target || item?.bookingTarget || 0;
        const arCollectionAchieved = item?.arCollection?.achieved || item?.arAchieved || 0;
        const arCollectionTarget = item?.arCollection?.target || item?.arTarget || 0;

        const newBookingPercentage =
          newBookingTarget > 0 ? (newBookingAchieved / newBookingTarget) * 100 : 0;
        const arCollectionPercentage =
          arCollectionTarget > 0 ? (arCollectionAchieved / arCollectionTarget) * 100 : 0;

        // Calculate isBad flags in frontend based on percentage < 100%
        const newBookingIsBad = newBookingPercentage < 100;
        const arCollectionIsBad = arCollectionPercentage < 100;
        const overallIsBad = newBookingIsBad || arCollectionIsBad;

        return {
          name,
          level, // Include level for filtering
          salesId: item?.id || item?.salesId || item?.user_id || item?.userId || null, // Get sales ID
          isBad: overallIsBad, // Overall isBad flag calculated in frontend
          newBooking: {
            achieved: newBookingAchieved,
            target: newBookingTarget,
            percentage: Math.round(newBookingPercentage * 10) / 10,
            isBad: newBookingIsBad // Booking isBad flag calculated in frontend
          },
          arCollection: {
            achieved: arCollectionAchieved,
            target: arCollectionTarget,
            percentage: Math.round(arCollectionPercentage * 10) / 10,
            isBad: arCollectionIsBad // AR isBad flag calculated in frontend
          }
        };
      });
    }

    return null;
  };

  // Transform all API data
  const allTargetAchievementData = transformTargetAchievementData(targetAchievementData) || [];

  // Filter data based on active tab
  // Executive Wise = Low level, Manager Wise = Medium level
  const executiveData = allTargetAchievementData.filter((item) => {
    if (activeTab === 'Executive Wise') {
      return item.level === 'Low' || !item.level; // Show executives (Low level) or items without level
    } else if (activeTab === 'Manager Wise') {
      return item.level === 'Medium'; // Show managers (Medium level)
    }
    return true; // Show all if tab doesn't match
  });

  // Filter reasons from props based on topic and salesId
  const getFilteredReasons = (topic, salesId = null) => {
    if (!allReasonsData || !Array.isArray(allReasonsData)) return [];

    return allReasonsData.filter((reason) => {
      const matchesTopic = reason.topic === topic;
      // If salesId is provided, filter by sales_id if available in reason
      // Otherwise, return all reasons for the topic
      if (salesId && reason.sales_id) {
        return matchesTopic && reason.sales_id === salesId;
      }
      return matchesTopic;
    });
  };

  // Modal state for Hot Leads and AR Deals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null); // 'newBooking' or 'arCollection'
  const [selectedSalesId, setSelectedSalesId] = useState(null);

  // State for inline editing - track which row is being edited
  const [editingRow, setEditingRow] = useState(null); // { type: 'hotLead' | 'arDeal', id: leadId or dealId }
  const [commentText, setCommentText] = useState('');

  // Track previous submitting state to detect when submission completes
  const prevIsSubmittingRef = useRef(isSubmittingReason);

  // API Query for Hot Leads and AR Deals (when modal opens)
  const {
    data: hotLeadsData,
    isLoading: isLoadingHotLeads,
    refetch: refetchHotLeads
  } = useQuery({
    queryKey: ['hotLeads', projectID, month, selectedSalesId],
    queryFn: () => getReportsHotLeadsNotConverted(projectID, month, selectedSalesId),
    enabled: !!projectID && !!month && !!selectedSalesId && showDetailsModal
  });

  // Handle info icon click
  const handleInfoClick = (exec, metric) => {
    setSelectedExecutive(exec);
    setSelectedMetric(metric);
    setSelectedSalesId(exec.salesId);
    setShowDetailsModal(true);
    mixpanel.track('Info Icon Clicked', {
      user_id: userID,
      executive: exec.name,
      metric: metric,
      sales_id: exec.salesId,
      timestamp: new Date().toISOString()
    });
  };

  // Handle calendar icon click (to add comment) - kept for backward compatibility
  const handleAddReasonClick = (leadId, dealId = null) => {
    // Determine topic based on whether dealId is present
    const topic = dealId ? 'Ar Not Captured' : 'Hot Leads Not Converted';
    handleOpenReasonModal(topic, leadId, dealId);
  };

  // Handle inline editing
  const handleStartEditing = (type, id, currentReason) => {
    setEditingRow({ type, id });
    setCommentText(currentReason || '');
  };

  const handleCancelEditing = () => {
    setEditingRow(null);
    setCommentText('');
  };

  const handleSaveComment = (leadId, dealId = null, reasonId = null) => {
    if (!commentText.trim()) {
      return;
    }
    const topic = dealId ? 'Ar Not Captured' : 'Hot Leads Not Converted';
    onSubmitReasonInline(topic, commentText, leadId, dealId, null, reasonId);
    // Editing state will be reset in useEffect after successful save
  };

  // Reset editing state when submission completes successfully
  useEffect(() => {
    if (prevIsSubmittingRef.current && !isSubmittingReason && editingRow) {
      setEditingRow(null);
      setCommentText('');
    }
    prevIsSubmittingRef.current = isSubmittingReason;
  }, [isSubmittingReason, editingRow]);

  // Update commentText when reasons data changes (after refetch)
  useEffect(() => {
    if (editingRow && allReasonsData) {
      if (editingRow.type === 'hotLead') {
        const editingIdNum = Number(editingRow.id);
        const reason = allReasonsData.find((r) => {
          const reasonLeadIdNum = r.lead_id ? Number(r.lead_id) : null;
          return reasonLeadIdNum === editingIdNum && r.topic === 'Hot Leads Not Converted';
        });
        if (reason) {
          setCommentText(reason.reason || '');
        }
      } else if (editingRow.type === 'arDeal') {
        const editingIdNum = Number(editingRow.id);
        const reason = allReasonsData.find((r) => {
          if (r.topic !== 'Ar Not Captured') return false;
          const reasonDealIdNum = r.deal_id ? Number(r.deal_id) : null;
          return reasonDealIdNum === editingIdNum;
        });
        if (reason) {
          setCommentText(reason.reason || '');
        }
      }
    }
  }, [allReasonsData, editingRow]);

  // Helper function for status color
  const getStatusColor = (percentage) => {
    if (percentage >= 100) return 'text-green-600';
    return 'text-orange-600';
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {renderSectionHeader(1, 'Target vs Achievement')}

        {(mode === 'view' || !collapsedSections[1]) && (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b">
              <button
                onClick={() => setActiveTab('Executive Wise')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'Executive Wise'
                    ? 'bg-primary text-white rounded-t-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}>
                Executive Wise
              </button>
              <button
                onClick={() => setActiveTab('Manager Wise')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'Manager Wise'
                    ? 'bg-primary text-white rounded-t-lg'
                    : 'text-gray-600 hover:text-gray-900'
                }`}>
                Manager Wise
              </button>
            </div>

            {/* Content based on active tab - Show same structure for both tabs */}
            {(activeTab === 'Executive Wise' || activeTab === 'Manager Wise') && (
              <div className="relative">
                {executiveData.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 text-lg">
                      {projectID && !isLoadingTargetAchievement
                        ? `No ${activeTab === 'Executive Wise' ? 'executive' : 'manager'} target achievement data available for this project.`
                        : projectID
                          ? 'Loading target achievement data...'
                          : 'Please select a project to view target achievement data.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Container with responsive grid: 1-3 items take full width, 4+ items show 4 per row, max 2 rows visible */}
                    <div
                      className="grid gap-4 pb-4"
                      style={{
                        gridTemplateColumns:
                          executiveData.length === 1
                            ? '1fr'
                            : executiveData.length === 2
                              ? 'repeat(2, 1fr)'
                              : executiveData.length === 3
                                ? 'repeat(3, 1fr)'
                                : 'repeat(4, minmax(0, 1fr))',
                        maxHeight: executiveData.length > 8 ? '600px' : 'none',
                        overflowY: executiveData.length > 8 ? 'auto' : 'visible',
                        overflowX: 'hidden'
                      }}>
                      {executiveData.map((exec, index) => (
                        <div
                          key={index}
                          className={`rounded-lg p-5 border hover:shadow-md transition-shadow relative ${
                            exec.isBad ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                          }`}>
                          <div className="flex items-center justify-between mb-5">
                            <h3 className="font-semibold text-gray-900 text-lg">{exec.name}</h3>
                            {/* Preview button - only show when isBad is true */}
                            {exec.isBad && (
                              <button
                                onClick={() => {
                                  handleInfoClick(exec, 'newBooking');
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm">
                                <svg
                                  className="w-4 h-4 text-gray-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                                Preview
                              </button>
                            )}
                          </div>

                          {/* New Booking */}
                          <div className="mb-5 relative">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">New Booking</span>
                              <span
                                className={`text-sm font-bold ${getStatusColor(exec.newBooking.percentage)}`}>
                                {exec.newBooking.percentage}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mb-2 relative overflow-hidden">
                              <div
                                className="h-3 rounded-full transition-all duration-500 bg-[#FFA8A1]"
                                style={{
                                  width: `${Math.min(exec.newBooking.percentage, 100)}%`,
                                  minWidth: '4px'
                                }}
                              />
                              {exec.newBooking.percentage > 100 && (
                                <div
                                  className="h-3 rounded-full bg-green-400 absolute top-0 left-full"
                                  style={{
                                    width: `${Math.min(exec.newBooking.percentage - 100, 20)}%`
                                  }}
                                />
                              )}
                            </div>
                            <div className="flex justify-between items-center">
                              <p className="text-xs text-gray-600">
                                {formatIndianCurrency(exec.newBooking.achieved)} /
                                {formatIndianCurrency(exec.newBooking.target)}
                              </p>
                            </div>
                          </div>

                          {/* AR Collection */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                AR Collection
                              </span>
                              <span
                                className={`text-sm font-bold ${getStatusColor(exec.arCollection.percentage)}`}>
                                {exec.arCollection.percentage}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mb-2 relative overflow-hidden">
                              <div
                                className="h-3 rounded-full transition-all duration-500 bg-[#a194ff]"
                                style={{
                                  width: `${Math.min(exec.arCollection.percentage, 100)}%`,
                                  minWidth: '4px'
                                }}
                              />
                              {exec.arCollection.percentage > 100 && (
                                <div
                                  className="h-3 rounded-full bg-green-400 absolute top-0 left-full"
                                  style={{
                                    width: `${Math.min(exec.arCollection.percentage - 100, 20)}%`
                                  }}
                                />
                              )}
                            </div>
                            <div className="flex justify-between items-center">
                              <p className="text-xs text-gray-600">
                                {formatIndianCurrency(exec.arCollection.achieved)} /
                                {formatIndianCurrency(exec.arCollection.target)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Scroll indicator for more than 8 items */}
                    {executiveData.length > 8 && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none flex items-end justify-center pb-2">
                        <span className="text-xs text-gray-500 bg-white/80 px-3 py-1 rounded-full shadow-sm">
                          Scroll for more ↓
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
        {/* Section Note */}
        {sectionNote && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {sectionNote.user?.vertical || 'Management'} notes
                  </h4>
                  <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                    <span className="text-xs text-gray-600">By</span>
                    <span className="text-xs font-medium text-gray-900">
                      {sectionNote.user?.name || 'Unknown User'}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{sectionNote.note}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hot Leads & AR Deals Modal */}
      {showDetailsModal && selectedExecutive && (
        <Modal
          name={`${selectedExecutive.name} - Lead & AR Details`}
          width={80}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedExecutive(null);
            setSelectedMetric(null);
            setSelectedSalesId(null);
          }}>
          <div className="">
            {/* Hot Leads Not Converted Section - only for newBooking */}
            {selectedMetric === 'newBooking' && (
              <div className="mb-8">
                <h3 className="font-bold text-gray-900 mb-4">Hot Leads Not Converted</h3>
                {isLoadingHotLeads ? (
                  <div className="flex justify-center items-center h-[8rem]">
                    <p className="text-sm text-gray-500">Loading hot leads...</p>
                  </div>
                ) : !hotLeadsData?.data?.hotLeads?.length &&
                  !hotLeadsData?.data?.hot_leads?.length &&
                  !hotLeadsData?.data?.leads?.length &&
                  !(Array.isArray(hotLeadsData?.data) && hotLeadsData?.data?.length > 0) ? (
                  <div className="flex justify-center items-center h-[8rem]">
                    <p className="text-sm text-gray-500">No hot leads available</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Customer
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Expected Closure
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Comment
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(() => {
                          const data = hotLeadsData?.data;
                          const leads = data?.hotLeads || data?.hot_leads || data?.leads || [];
                          // Get all reasons for this topic (don't filter by salesId, match by lead_id instead)
                          const allTopicReasons = getFilteredReasons(
                            'Hot Leads Not Converted',
                            null
                          );

                          return leads.map((lead, index) => {
                            // Check if reason exists for this specific lead_id
                            // Normalize IDs to numbers for comparison
                            const leadIdNum = Number(lead.id);
                            const existingReason = allTopicReasons.find((r) => {
                              const reasonLeadIdNum = r.lead_id ? Number(r.lead_id) : null;
                              return (
                                reasonLeadIdNum === leadIdNum &&
                                r.topic === 'Hot Leads Not Converted'
                              );
                            });
                            const hasReason = !!existingReason;

                            return (
                              <tr key={lead.id || index}>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  <div className="font-medium">{lead.user?.name || 'N/A'}</div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {lead.expected_clouser_date
                                    ? new Date(lead.expected_clouser_date).toLocaleDateString(
                                        'en-IN',
                                        {
                                          day: 'numeric',
                                          month: 'long',
                                          year: 'numeric'
                                        }
                                      )
                                    : 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                  {isLoadingReasons ? (
                                    <span className="text-gray-400">Loading...</span>
                                  ) : editingRow?.type === 'hotLead' &&
                                    String(editingRow?.id) === String(lead.id) ? (
                                    <div className="space-y-2">
                                      <textarea
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        placeholder="Enter your comment..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                        rows={3}
                                        autoFocus
                                      />
                                      <div className="flex gap-2">
                                        <Button
                                          onClick={() =>
                                            handleSaveComment(
                                              lead.id,
                                              null,
                                              existingReason?.id || null
                                            )
                                          }
                                          displayType="secondary-red"
                                          disabled={!commentText.trim() || isSubmittingReason}
                                          className="text-xs px-3 py-1">
                                          {isSubmittingReason ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button
                                          onClick={handleCancelEditing}
                                          displayType="secondary"
                                          disabled={isSubmittingReason}
                                          className="text-xs px-3 py-1">
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : existingReason?.reason ? (
                                    <div
                                      onClick={
                                        mode === 'dashboard' && canAddReason
                                          ? () =>
                                              handleStartEditing(
                                                'hotLead',
                                                lead.id,
                                                existingReason.reason
                                              )
                                          : undefined
                                      }
                                      className={`${
                                        mode === 'dashboard' && canAddReason
                                          ? 'cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors'
                                          : ''
                                      }`}>
                                      {existingReason.reason}
                                    </div>
                                  ) : (
                                    <div
                                      onClick={
                                        mode === 'dashboard' && canAddReason
                                          ? () => handleStartEditing('hotLead', lead.id, '')
                                          : undefined
                                      }
                                      className={`text-gray-400 italic ${
                                        mode === 'dashboard' && canAddReason
                                          ? 'cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors hover:text-gray-600'
                                          : ''
                                      }`}>
                                      {mode === 'dashboard' && canAddReason
                                        ? 'Click here to add/edit reason'
                                        : 'N/A'}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* AR Deals - Payment Not Captured Section */}
            <div>
              <h3 className="font-bold text-gray-900 mb-4">AR Deals - Payment Not Received</h3>
              {isLoadingHotLeads ? (
                <div className="flex justify-center items-center h-[8rem]">
                  <p className="text-sm text-gray-500">Loading AR deals...</p>
                </div>
              ) : !hotLeadsData?.data?.arDeals?.length &&
                !hotLeadsData?.data?.ar_deals?.length &&
                !hotLeadsData?.data?.arNotCaptured?.length &&
                !hotLeadsData?.data?.ar_not_captured?.length &&
                !hotLeadsData?.data?.ar?.length ? (
                <div className="flex justify-center items-center h-[8rem]">
                  <p className="text-sm text-gray-500">No AR deals available</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Customer
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Expected Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Paid Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Due Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Last Payment Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Comment
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(() => {
                        const data = hotLeadsData?.data;
                        const arDeals =
                          data?.ar ||
                          data?.arDeals ||
                          data?.ar_deals ||
                          data?.arNotCaptured ||
                          data?.ar_not_captured ||
                          [];
                        // Get all reasons for this topic (don't filter by salesId, match by lead_id/deal_id instead)
                        const allTopicReasons = getFilteredReasons('Ar Not Captured', null);

                        return arDeals.map((ar, index) => {
                          const leadId = ar.closedDeal?.lead?.id || null;
                          const dealId = ar.deal_id || ar.closedDeal?.id || null;
                          // Use AR deal's own ID as unique identifier (fallback to index if no ID)
                          // Always convert to string for consistent comparison
                          const arDealId = String(ar.id || `ar-${index}`);

                          // Normalize IDs to numbers for comparison
                          const leadIdNum = leadId ? Number(leadId) : null;
                          const dealIdNum = dealId ? Number(dealId) : null;

                          // Check if reason exists for this specific deal (match by lead_id or deal_id)
                          const existingReason = allTopicReasons.find((r) => {
                            if (r.topic !== 'Ar Not Captured') return false;

                            const reasonLeadIdNum = r.lead_id ? Number(r.lead_id) : null;
                            const reasonDealIdNum = r.deal_id ? Number(r.deal_id) : null;

                            // Match by lead_id if both exist
                            if (leadIdNum && reasonLeadIdNum === leadIdNum) return true;
                            // Match by deal_id if both exist
                            if (dealIdNum && reasonDealIdNum === dealIdNum) return true;

                            return false;
                          });
                          const hasReason = !!existingReason;

                          const customerName = ar.closedDeal?.lead?.user?.name || 'N/A';
                          const amount = ar.amount || '0';
                          const lastPayment = ar.lastPaymentDate || null;
                          const dueDate = ar.due_date || null;
                          const due_amount = parseFloat(amount) - parseFloat(ar.paid_amount);
                          return (
                            <tr key={ar.id || index}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <div className="font-medium">{customerName}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatIndianCurrency(amount || 0)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatIndianCurrency(ar.paid_amount || 0)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatIndianCurrency(due_amount || 0)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {lastPayment
                                  ? new Date(lastPayment).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'long',
                                      year: 'numeric'
                                    })
                                  : dueDate
                                    ? new Date(dueDate).toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'long',
                                        year: 'numeric'
                                      })
                                    : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                {isLoadingReasons ? (
                                  <span className="text-gray-400">Loading...</span>
                                ) : editingRow?.type === 'arDeal' &&
                                  String(editingRow?.id) === String(arDealId) ? (
                                  <div className="space-y-2">
                                    <textarea
                                      value={commentText}
                                      onChange={(e) => setCommentText(e.target.value)}
                                      placeholder="Enter your comment..."
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                      rows={3}
                                      autoFocus
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        onClick={() =>
                                          handleSaveComment(
                                            leadId,
                                            dealId,
                                            existingReason?.id || null
                                          )
                                        }
                                        displayType="secondary-red"
                                        disabled={!commentText.trim() || isSubmittingReason}
                                        className="text-xs px-3 py-1">
                                        {isSubmittingReason ? 'Saving...' : 'Save'}
                                      </Button>
                                      <Button
                                        onClick={handleCancelEditing}
                                        displayType="secondary"
                                        disabled={isSubmittingReason}
                                        className="text-xs px-3 py-1">
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : existingReason?.reason ? (
                                  <div
                                    onClick={
                                      mode === 'dashboard' && canAddReason
                                        ? () =>
                                            handleStartEditing(
                                              'arDeal',
                                              arDealId,
                                              existingReason.reason
                                            )
                                        : undefined
                                    }
                                    className={`${
                                      mode === 'dashboard' && canAddReason
                                        ? 'cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors'
                                        : ''
                                    }`}>
                                    {existingReason.reason}
                                  </div>
                                ) : (
                                  <div
                                    onClick={
                                      mode === 'dashboard' && canAddReason
                                        ? () => handleStartEditing('arDeal', arDealId, '')
                                        : undefined
                                    }
                                    className={`text-gray-400 italic ${
                                      mode === 'dashboard' && canAddReason
                                        ? 'cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors hover:text-gray-600'
                                        : ''
                                    }`}>
                                    {mode === 'dashboard' && canAddReason
                                      ? 'Click here to add/edit reason'
                                      : 'N/A'}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Section1;
